import {useCallback, useEffect, useState} from 'react';
import './Range.sass';

const calculatePercentage = (value, min, max) => {
  const percent = ((value - min) * 100) / (max - min);
  return `${percent}% 100%`;
};

export const Range = ({
  min = 0,
  max = 100,
  step = 1,
  onChange,
  value = 1,
  suffix,
  disabled = false,
  dataQaId,
  onLocalChange,
  inputEditable = false,
  hiddenInput = false,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [backgroundSize, setBackgroundSize] = useState(calculatePercentage(value, min, max));

  const calculateValue = useCallback(
    (newValue) => {
      if (Number.isNaN(newValue)) {
        return newValue;
      }
      if (Number(newValue) < min) {
        return min;
      }
      if (Number(newValue) > max) {
        return max;
      }

      return Number(newValue);
    },
    [max, min]
  );

  const isInvalid = (newValue) =>
    !!(Number.isNaN(newValue) || Number(newValue) < min || Number(newValue) > max);

  const parseNumber = useCallback(
    (newValue) => {
      const number = calculateValue(newValue);
      return Number.isNaN(number) ? 0 : number;
    },
    [calculateValue]
  );

  const onChangeHandler = useCallback(
    (e) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const onLocalChangeHandler = useCallback(
    (e) => {
      const targetValue = e.target.value;
      setLocalValue(targetValue);
      onLocalChange(parseNumber(targetValue));
      setBackgroundSize(calculatePercentage(parseNumber(targetValue), min, max));
    },
    [onLocalChange, min, max, parseNumber]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setBackgroundSize(calculatePercentage(value, min, max));
    setLocalValue(value);
  }, [value]);

  const onBlurHandler = useCallback(() => {
    const newValue = parseNumber(localValue);
    setLocalValue(newValue);
    onChange(newValue);
  }, [localValue, onChange, parseNumber]);

  return (
    <div className="range">
      <input
        type="range"
        min={disabled ? 0 : min}
        max={max}
        value={disabled ? 0 : parseNumber(localValue)}
        onChange={onChangeHandler}
        step={step}
        disabled={disabled}
        data-qa-id={dataQaId}
        style={{backgroundSize}}
      />
      {!hiddenInput && (
        <div
          className={`range__value ${
            !disabled && isInvalid(localValue) ? 'range__value--invalid' : ''
          }`}
        >
          <input
            type="number"
            inputMode="numeric"
            value={disabled ? 0 : `${localValue}`}
            style={{width: `${localValue?.toString().length}ch`}}
            onChange={inputEditable ? onLocalChangeHandler : undefined}
            onBlur={onBlurHandler}
            disabled={!inputEditable}
            data-qa-id={`${dataQaId}-input`}
          />
          <span className={!inputEditable ? 'range__disabled-suffix' : ''}>{suffix && suffix}</span>
        </div>
      )}
    </div>
  );
};
